import { getZrmLeaseInfo, getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { CS_EVENT_TRIGGER_LOCATION } from '@zg-rentals/modal-tenant';

import { _nextTrackEvent } from '../index';

import type { ZrmClickstreamLease, ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export { CS_EVENT_TRIGGER_LOCATION };

const EVENT_IDS = {
  ADD_TENANT_STEP_CLICK: '8891',
  DEACTIVATION_REASON_CLICK: '8890',
  LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK: '8892',
  TENANCY_PERIOD_CREATED: '8369',
  TENANCY_PERIOD_EDIT_CLICK: '8370',
  TENANCY_PERIOD_EDIT_SUCCESS: '8371',
  TENANTS_MODULE_ADD_TENANT_CLICK: '7908',
  TENANTS_MODULE_SAVE_CLICK: '7909',
  TENANT_ACCORDION_ITEM_ADD_TENANCY_PERIOD_CLICK: '8368',
  TENANT_ACCORDION_ITEM_EXPAND_CLICK: '8877',
  TENANT_DIRECTORY_EDIT_CLICK: '8182',
} as const;

export const ADD_TENANT_STEP_TRIGGER_SOURCE = {
  SAVE_AND_CONTINUE: 'button_save_and_continue',
  SKIP: 'button_skip',
  CLOSE: 'button_close',
} as const;

export const LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE = {
  CREATE_LEASE: 'button_create_lease',
  SETUP_PAYMENTS: 'button_setup_payments',
  CLOSE: 'button_close',
} as const;

export const TENANT_ACCORDION_ITEM_EXPAND_CLICK_EVENT_ID = EVENT_IDS.TENANT_ACCORDION_ITEM_EXPAND_CLICK;
export const TENANTS_MODULE_ADD_TENANT_CLICK_EVENT_ID = EVENT_IDS.TENANTS_MODULE_ADD_TENANT_CLICK;
export const TENANT_ACCORDION_ITEM_ADD_TENANCY_PERIOD_CLICK_EVENT_ID =
  EVENT_IDS.TENANT_ACCORDION_ITEM_ADD_TENANCY_PERIOD_CLICK;
export const TENANCY_PERIOD_EDIT_CLICK_EVENT_ID = EVENT_IDS.TENANCY_PERIOD_EDIT_CLICK;
export const TENANT_DIRECTORY_EDIT_CLICK_EVENT_ID = EVENT_IDS.TENANT_DIRECTORY_EDIT_CLICK;
export const LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK_EVENT_ID = EVENT_IDS.LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK;
export const ADD_TENANT_STEP_CLICK_EVENT_ID = EVENT_IDS.ADD_TENANT_STEP_CLICK;
export const DEACTIVATION_REASON_CLICK_EVENT_ID = EVENT_IDS.DEACTIVATION_REASON_CLICK;

// https://dataportal.zillowgroup.net/event-registry/7908
export const emitTenantsModuleAddTenantClickEvent = ({
  user,
  currentProperty,
  triggerLocation,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerLocation: (typeof CS_EVENT_TRIGGER_LOCATION)[keyof typeof CS_EVENT_TRIGGER_LOCATION];
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.TENANTS_MODULE_ADD_TENANT_CLICK,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'add_tenant',
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_rentals_tenants'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7909
export const emitTenantsModuleSaveClickEvent = ({
  user,
  currentProperty,
  triggerLocation,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerLocation: (typeof CS_EVENT_TRIGGER_LOCATION)[keyof typeof CS_EVENT_TRIGGER_LOCATION];
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '171',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.TENANTS_MODULE_SAVE_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'save',
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_rentals_tenants'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8368
export const emitTenantAccordionItemAddTenancyPeriodClickEvent = ({
  user,
  currentProperty,
  lease,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
}) =>
  _nextTrackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.TENANT_ACCORDION_ITEM_ADD_TENANCY_PERIOD_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'add_tenancy_period',
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_rentals_tenants'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/8370
export const emitTenancyPeriodEditClickEvent = ({
  user,
  currentProperty,
  lease,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
}) =>
  _nextTrackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.TENANCY_PERIOD_EDIT_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'edit_tenancy_period',
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_rentals_tenants'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/8182
export const emitTenantAccordionItemEditClickEvent = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.TENANT_DIRECTORY_EDIT_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'zrm_property_overview',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'edit',
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_rentals_tenants'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8877
export const emitTenantAccordionItemExpandClickEvent = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.TENANT_ACCORDION_ITEM_EXPAND_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'zrm_property_overview',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_expand_tenant_details',
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_rentals_tenants'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8890
export const emitDeactivationReasonClickEvent = ({
  user,
  currentProperty,
  triggerObject,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerObject: 'found_new_tenant' | 'tenant_renewed' | 'still_looking';
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DEACTIVATION_REASON_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'button_to_deactivate',
    },
    semantic: {
      semantic_event_nm: 'deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8892
export const emitLeaseAndPaymentUpsellStepClickEvent = ({
  user,
  currentProperty,
  triggerSource,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerSource: (typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE)[keyof typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE];
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8891
export const emitAddTenantStepClickEvent = ({
  user,
  currentProperty,
  triggerSource,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
  triggerSource: (typeof ADD_TENANT_STEP_TRIGGER_SOURCE)[keyof typeof ADD_TENANT_STEP_TRIGGER_SOURCE];
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.ADD_TENANT_STEP_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'tenant_directory',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
